'use client'

import * as React from 'react'
import { usePathname, useSearchParams } from 'next/navigation'

import { trackAfterTitleSettled } from '@/helpers/waitForTitleSettled'
import { useSiteContext } from '@/hooks/useSiteContext'
import { useTrackEvent } from '@/hooks/useTrackEvent'
import { LanguageContext } from '@/providers/TranslationContextProvider'

export const usePageViewTracking = () => {
  const { trackPageView } = useTrackEvent()
  const { siteContext } = useSiteContext()
  const { language } = React.useContext(LanguageContext)
  const lastLoggedPath = React.useRef<string>('')
  const lastTrackedTitle = React.useRef<string | undefined>(undefined)

  const pathname = usePathname()
  const searchParams = useSearchParams()
  const asPath = pathname + (searchParams.size > 0 ? `?${searchParams.toString()}` : '')

  React.useEffect(() => {
    const currentPath = asPath

    if (lastLoggedPath.current === currentPath) {
      return
    }

    const controller = new AbortController()

    void trackAfterTitleSettled(lastTrackedTitle.current, controller.signal, (title) => {
      lastLoggedPath.current = currentPath
      lastTrackedTitle.current = title
      trackPageView()
    })

    return () => controller.abort()
  }, [asPath, language, siteContext, trackPageView])
}
